import axios from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getAreaRangeData,
  getCompassAPIChartData,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  magnitudePointFormatter,
} from '../utils/compass'
import { Palette, chartPalette, datetimeXAxis } from '../utils/constants'
import { capitalizeString, formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const RAIL_CHARTS_PATH = '/api/charts/rail'

type GrainsChartId =
  | 'stock'
  | 'consumption'
  | 'stock_consumption_ratio'
  | 'imports_exports'
  | 'grain_meat_demand_ratio'

const GRAINS_CHART_PATHS: Record<GrainsChartId, string> = {
  stock: '/grains/stock',
  consumption: '/grains/consumption',
  stock_consumption_ratio: '/grains/stock-consumption-ratio',
  imports_exports: '/grains/imports-exports',
  grain_meat_demand_ratio: '/grains/grain-meat-demand-ratio',
}

export namespace Rail {
  export namespace Imea {
    const CONTEXT_ID = 'equity:rail:imea'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'spread_route' | 'spread_price_soybean' | 'spread_price_corn'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getSpreadChartProps(config, data),
    }

    const getSpreadChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        yAxis: [
          {
            title: {
              text: config.chart === 'spread_route' ? 'R$/ton' : '$/ton',
            },
          },
        ],
      })(config, data)
  }

  export namespace Conab {
    const CONTEXT_ID = 'equity:rail:conab'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'harvest_projection_area_brasil_soja'
        | 'harvest_projection_produtividade_brasil_soja'
        | 'harvest_projection_producao_brasil_soja'
        | 'estimates_error_area_brasil_soja'
        | 'estimates_error_produtividade_brasil_soja'
        | 'estimates_error_producao_brasil_soja'
        | 'harvest_projection_area_brasil_milho'
        | 'harvest_projection_produtividade_brasil_milho'
        | 'harvest_projection_producao_brasil_milho'
        | 'estimates_error_area_brasil_milho'
        | 'estimates_error_produtividade_brasil_milho'
        | 'estimates_error_producao_brasil_milho'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'harvest_projection_area_brasil_soja':
          case 'harvest_projection_produtividade_brasil_soja':
          case 'harvest_projection_producao_brasil_soja':
            return getHarvestProjectionSojaChartProps(config, data)
          case 'estimates_error_area_brasil_soja':
          case 'estimates_error_produtividade_brasil_soja':
          case 'estimates_error_producao_brasil_soja':
          case 'estimates_error_area_brasil_milho':
          case 'estimates_error_produtividade_brasil_milho':
          case 'estimates_error_producao_brasil_milho':
            return getEstimatesErrorChartProps(config, data)
          case 'harvest_projection_area_brasil_milho':
          case 'harvest_projection_produtividade_brasil_milho':
          case 'harvest_projection_producao_brasil_milho':
            return getHarvestProjectionMilhoChartProps(config, data)
        }
      },
    }

    const getHarvestProjectionSojaChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: [false, true],
          yAxis: [
            {
              id: 'absolute',
              title: {
                text:
                  config.chart === 'harvest_projection_area_brasil_soja'
                    ? '1000 ha'
                    : config.chart ===
                      'harvest_projection_produtividade_brasil_soja'
                    ? 'kg/ha'
                    : '1000 ton',
              },
            },
            { id: 'relative', labelSuffix: '%', opposite: true },
          ],
        },
        {
          yAxis: ['absolute', 'relative'],
          type: ['spline', 'spline'],
        }
      )(config, data)

    const getEstimatesErrorChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: [true],
          yAxis: [{ id: 'relative', labelSuffix: '%' }],
        },
        {
          type: ['spline'],
          yAxis: ['relative'],
        }
      )(config, data)

    const getHarvestProjectionMilhoChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: [false, false, false, false, true],
          yAxis: [
            {
              id: 'absolute',
              title: {
                text:
                  config.chart === 'harvest_projection_area_brasil_milho'
                    ? '1000 ha'
                    : config.chart ===
                      'harvest_projection_produtividade_brasil_milho'
                    ? 'kg/ha'
                    : '1000 ton',
              },
            },
            { id: 'relative', labelSuffix: '%', opposite: true },
          ],
        },
        {
          color: [
            Palette.darkGray,
            Palette.darkYellow,
            Palette.orange,
            Palette.darkBlue,
            Palette.darkGreen,
          ],
          yAxis: ['absolute', 'absolute', 'absolute', 'absolute', 'relative'],
          type: ['area', 'area', 'area', 'spline', 'spline'],
        }
      )(config, data)
  }

  export namespace WasdeGrains {
    const CONTEXT_ID = 'equity:rail:wasde_grains'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'area_soy'
        | 'productivity_soy'
        | 'production_soy'
        | 'area_corn'
        | 'productivity_corn'
        | 'production_corn'
        | 'prices_soy'
        | 'prices_corn'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: capitalizeString(data.title),
          convertToPercentages: [false, false, true],
          yAxis: [
            {
              id: 'absolute',
              title: {
                text:
                  config.chart === 'area_soy' || config.chart === 'area_corn'
                    ? '1000 ha'
                    : config.chart === 'productivity_soy' ||
                      config.chart === 'productivity_corn'
                    ? 'kg/ha'
                    : '1000 tons',
              },
            },
            { id: 'relative', labelSuffix: '%', opposite: true },
          ],
        },
        {
          color: [Palette.darkBlue, Palette.darkGray, Palette.darkGreen],
          dashStyle: ['Solid', 'ShortDot', 'Solid'],
          type: ['spline', 'spline', 'spline'],
          yAxis: ['absolute', 'absolute', 'relative'],
        }
      )(config, data)
  }

  export namespace BrazilExports {
    const CONTEXT_ID = 'equity:rail:brazil_exports'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'santos_share_all_states'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getCetipStockYoyChartProps(config, data),
    }

    const getCetipStockYoyChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [true, true, true],
      },
      {
        type: ['spline'],
      }
    )
  }

  export namespace ChinaImports {
    const CONTEXT_ID = 'equity:rail:china_imports'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'soya_beans_quantity' | 'soya_beans_value'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getCetipStockYoyChartProps(config, data),
    }

    const getCetipStockYoyChartProps = getMultipleLineChartProps(
      Context,
      {
        yAxis: [{ title: { text: '1000 ton' } }],
      },
      {
        type: ['spline', 'spline'],
      }
    )
  }

  export namespace BrazilExportsLogisticMt {
    const CONTEXT_ID = 'equity:rail:brazil_exports_logistic_mt'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      chart: 'port_mt_share' | 'port_mt_volume'
      field: string
      grain: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/brazil-exports/logistic-mt',
        method: 'POST',
        data: { field: config.field, grain: config.grain },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(
            config.chart === 'port_mt_share'
          ),
          yAxis: [
            {
              title: {
                text: config.chart === 'port_mt_volume' ? '1000 ton' : '',
              },
              labelSuffix: '%',
            },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }

  export namespace BrazilExportsSantosShareFromState {
    const CONTEXT_ID = 'equity:rail:brazil_exports_santos_share_from_state'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      state: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/brazil-exports/santos-share',
        method: 'GET',
        params: { state: config.state },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.state}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }

  export namespace BrazilExportsStatePort {
    const CONTEXT_ID = 'equity:rail:brazil_exports_state_port'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      grainType: 'SOY' | 'SOY BRAN' | 'CORN'
      port: string
      uf: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/brazil-exports/state-port',
        method: 'POST',
        data: {
          grain_type: config.grainType,
          port: config.port,
          uf: config.uf,
        },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      const CONVERTION_FACTOR = 1e3
      const convertDataPoint = (point: { date: string; value: number }) => [
        new Date(point.date).getTime(),
        point.value / CONVERTION_FACTOR,
      ]

      if (data.data && data.data?.[0]?.data.length > 0) {
        data.data?.[0].data.map(convertDataPoint).forEach(([x, value]) => {
          rangeValues[x] = { min: value }
        })
      }
      if (data.data && data.data?.[1]?.data.length > 0) {
        data.data?.[1].data.map(convertDataPoint).forEach(([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        })
      }

      const getCommonSeriesConfig = (data: ChartData['data'], idx: number) => ({
        id: 'series-' + idx,
        name: data[idx].name,
        data: data[idx].data.map(convertDataPoint),
        marker: { enabled: false },
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          valueDecimals: 2,
          xDateFormat: '%A, %b %e, %Y',
        },
        type: 'spline',
        yAxis: 'default',
        zIndex: 1,
      })

      const filteredPalette = chartPalette.filter(
        (color) => color !== Palette.fadedGreen
      )

      const series =
        data.data && data.data?.length > 1
          ? [
              ...Array.from(Array(data.data.length - 2).keys()).map((idx) => ({
                color: filteredPalette[idx % filteredPalette.length],
                ...getCommonSeriesConfig(data.data, idx + 2),
              })),
              {
                color: Palette.fadedGreen,
                data: getAreaRangeData(rangeValues),
                enableMouseTracking: false,
                grouping: false,
                id: `series-range`,
                marker: { enabled: false },
                name: data.data?.[0]?.name,
                type: 'arearange',
                yAxis: 'default',
                xAxis: 0,
                zIndex: 0,
              },
            ]
          : []

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'ton' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ConabCommodityByRegion {
    const CONTEXT_ID = 'equity:rail:conab_commodity_by_region'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type ConabChartId =
      | 'soybean_area'
      | 'soybean_produtividade'
      | 'soybean_producao'
      | 'corn_area'
      | 'corn_produtividade'
      | 'corn_producao'

    export interface ChartConfig {
      title?: string
      chart: ConabChartId
      selectedOptions: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const CONAB_CHART_PATHS: Record<ConabChartId, string> = {
      soybean_area: '/conab/soybean-area',
      soybean_produtividade: '/conab/soybean-produtividade',
      soybean_producao: '/conab/soybean-producao',
      corn_area: '/conab/corn-area',
      corn_produtividade: '/conab/corn-produtividade',
      corn_producao: '/conab/corn-producao',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + CONAB_CHART_PATHS[config.chart],
        method: 'GET',
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isSelected = (series: string) =>
        config.selectedOptions
          .map((option) => [option, option + ' (PREVISAO)', option + ' (YoY)'])
          .flat()
          .includes(series)

      const series = data.data
        .filter((s) => isSelected(s.name))
        .map((s, idx) => ({
          color: s.name.includes('(YoY)')
            ? Palette.darkGreen
            : s.name.includes('(PREVISAO)')
            ? Palette.darkGray
            : Palette.darkBlue,
          data: convertCompassToHighchartsDataFormat(s.data, {
            convertPercentage: s.name.includes('(YoY)'),
          }),
          id: `series-${idx}`,
          marker: { enabled: false },
          name: s.name,
          type: 'spline',
          tooltip: {
            valueDecimals: 2,
            xDateFormat: '%A, %b %e, %Y',
            ...(s.name.includes('(YoY)') ? { valueSuffix: '%' } : {}),
          },
          yAxis: s.name.includes('(YoY)') ? 'relative' : 'absolute',
        }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'absolute',
          opposite: false,
          title: {
            text: config.chart.includes('area')
              ? '1000 ha'
              : config.chart.includes('produtividade')
              ? 'kg/ha'
              : '1000 ton',
          },
          startOnTick: false,
          endOnTick: false,
        },
        {
          id: 'relative',
          labels: { format: '{value}%' },
          opposite: true,
          title: { text: '' },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ConabSafrasCorn {
    const CONTEXT_ID = 'equity:rail:conab_safras_corn'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      field: 'AREA' | 'PRODUTIVIDADE' | 'PRODUCAO'
      region: string
      typeChart: 'percentage' | 'value'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/conab/safras-corn',
        method: 'POST',
        data: {
          field: config.field,
          region: config.region,
          type_chart: config.typeChart,
        },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(
            config.typeChart === 'percentage'
          ),
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: {
                text:
                  config.field === 'AREA'
                    ? '1000 ha'
                    : config.field === 'PRODUTIVIDADE'
                    ? 'kg/ha'
                    : '1000 ton',
              },
              ...(config.typeChart === 'percentage'
                ? {
                    labelSuffix: '%',
                  }
                : {}),
            },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }

  export namespace GrainsCountry {
    const CONTEXT_ID = 'equity:rail:grains_country'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      chart: GrainsChartId
      country: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + GRAINS_CHART_PATHS[config.chart],
        method: 'GET',
        params: { country: config.country },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${config.country}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: { text: '1000 MT' },
            },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)
  }

  export namespace GrainsCountryCommodity {
    const CONTEXT_ID = 'equity:rail:grains_country_commodity'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      chart: GrainsChartId
      country: string
      commodityType: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + GRAINS_CHART_PATHS[config.chart],
        method: 'POST',
        data: { country: config.country, commodity_type: config.commodityType },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: {
                text:
                  config.chart === 'stock' ||
                  config.chart === 'consumption' ||
                  config.chart === 'imports_exports'
                    ? '1000 MT'
                    : '% x 365',
              },
            },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
          name: data.data.map((s) => capitalizeString(s.name)),
        }
      )(config, data)
  }

  export namespace ImeaComercializacao {
    const CONTEXT_ID = 'equity:rail:imea_comercializacao'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      macroregion: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/imea/comercializacao',
        method: 'POST',
        data: {
          macroregion: config.macroregion,
        },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.macroregion}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
        },
        {
          type: Array(data.data.length).fill('line'),
        }
      )(config, data)
  }

  export namespace ImeaPrecoVenda {
    const CONTEXT_ID = 'equity:rail:imea_preco_venda'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      chart: 'preco_venda_soja' | 'preco_venda_milho'
      macroregion: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/imea/preco-venda',
        method: 'POST',
        data: {
          macroregion: config.macroregion,
          grain: config.chart === 'preco_venda_soja' ? 'Soja' : 'Milho',
        },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: { text: 'R$/ton' },
            },
          ],
        },
        {
          type: Array(data.data.length).fill('line'),
        }
      )(config, data)
  }

  export namespace ImeaRoutes {
    const CONTEXT_ID = 'equity:rail:imea_routes'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      url: string
      selectedOptions: string[]
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        yAxis: [
          {
            id: 'default',
            opposite: false,
            title: { text: 'R$/t' },
          },
        ],
      })(config, {
        ...data,
        data: data.data.filter((s) => config.selectedOptions.includes(s.name)),
      })
  }

  export namespace LivestockCountry {
    const CONTEXT_ID = 'equity:rail:livestock_country'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      country: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/livestock/consumption-per-capita',
        method: 'GET',
        params: { country: config.country },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.country}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          yAxis: [
            {
              id: 'default',
              opposite: false,
              title: { text: '' },
              labelSuffix: 'kg',
            },
          ],
          plotOptions: { area: { stacking: 'normal' } },
        },
        {
          type: Array(data.data.length).fill('area'),
        }
      )(config, formatSeriesNames(data))
  }

  export namespace LivestockCountryCommodity {
    const CONTEXT_ID = 'equity:rail:livestock_country_commodity'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type LivestockChartId =
      | 'stock'
      | 'consumption'
      | 'imports_exports'
      | 'cagr_stock'
      | 'cagr_consumption'
      | 'cagr_imports_exports'
      | 'stock_consumption_ratio'
      | 'cagr_stock_consumption_ratio'
      | 'consumption_per_capita'

    export interface ChartConfig {
      title?: string
      chart: LivestockChartId
      country: string
      commodityType: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const LIVESTOCK_CHART_PATHS: Record<LivestockChartId, string> = {
      stock: '/livestock/stock',
      consumption: '/livestock/consumption',
      imports_exports: '/livestock/imports-exports',
      cagr_stock: '/livestock/cagr-stock',
      cagr_consumption: '/livestock/cagr-consumption',
      cagr_imports_exports: '/livestock/cagr-imports-exports',
      stock_consumption_ratio: '/livestock/stock-consumption-ratio',
      cagr_stock_consumption_ratio: '/livestock/cagr-stock-consumption-ratio',
      consumption_per_capita: '/livestock/consumption-per-capita',
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + LIVESTOCK_CHART_PATHS[config.chart],
        method: 'POST',
        data: { country: config.country, commodity_type: config.commodityType },
      }).then((response) => response.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) => {
      const isPercentage =
        config.chart === 'cagr_stock' ||
        config.chart === 'cagr_consumption' ||
        config.chart === 'cagr_imports_exports'

      return getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(isPercentage),
          yAxis: [
            {
              startOnTick: false,
              endOnTick: false,
              id: 'default',
              opposite: false,
              title: {
                text:
                  config.chart === 'stock' ||
                  config.chart === 'consumption' ||
                  config.chart === 'imports_exports'
                    ? '1000 MT (Carcass Weight Equivalent)'
                    : config.chart === 'stock_consumption_ratio' ||
                      config.chart === 'cagr_stock_consumption_ratio'
                    ? '% x 365'
                    : '',
              },
              labelSuffix: isPercentage ? '%' : '',
            },
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, formatSeriesNames(data))
    }
  }

  export namespace UsaExports {
    const CONTEXT_ID = 'equity:rail:usa_exports'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >
    export interface ChartConfig {
      title?: string
      containerType: 'exports' | 'yoy-exports'
      commodityType: 'SOYBEANS' | 'SOYBEAN CAKE & MEAL' | 'CORN'
      country: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url: RAIL_CHARTS_PATH + '/usa-exports/' + config.containerType,
        method: 'POST',
        data: { commodity_type: config.commodityType, country: config.country },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage:
            config.containerType === 'yoy-exports' && idx === 1,
        }),
        id: `series-${idx}`,
        marker: { enabled: false },
        name: s.name,
        type: 'spline',
        tooltip: {
          pointFormatter: magnitudePointFormatter(),
          valueDecimals: 2,
          xDateFormat: '%A, %b %e, %Y',
          ...(config.containerType === 'yoy-exports' && idx === 1
            ? { valueSuffix: '%' }
            : {}),
        },
        yAxis:
          config.containerType === 'yoy-exports' && idx === 1
            ? 'relative'
            : 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: 'ton' },
          startOnTick: false,
          endOnTick: false,
        },
        config.containerType === 'yoy-exports'
          ? {
              id: 'relative',
              opposite: true,
              labels: { format: '{value}%' },
              title: { text: '' },
              startOnTick: false,
              endOnTick: false,
            }
          : { title: { text: '' }, startOnTick: false, endOnTick: false },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
